import React, { useState, useEffect, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import { useContextApi } from "../../../../context/ContextApi";
import { PlusIcon } from "../../../../constants/Icons";

const AddSignature = ({
  onClose,
  data,
  ImageView,
  Ontrigger = true,
  onAdd,
}) => {
  const [imagePreview, setImagePreview] = useState(
    ImageView ? ImageView : "/assets/images/avatars/default.jpg"
  );
  const [imageFile, setImageFile] = useState(null);
  const [cropMode, setCropMode] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [finalOptions, setFinalOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const fileInputRef = useRef(null);
  const hasFetched = useRef(false);
  const { useEscapeKey } = useContextApi();
  useEscapeKey(onClose);

  useEffect(() => {
    if (data) {
      setImagePreview(
        typeof data === "string" ? data : URL.createObjectURL(data)
      );
      setImageFile(data);
    }
  }, [data]);

  const handleImageClick = () => fileInputRef.current?.click();

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageFile(file);
    setImageSrc(URL.createObjectURL(file));
    setCropMode(true);
  };

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Convert cropped area to a new base64 image
  const cropImage = (imageSrc, pixelCrop) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.src = imageSrc;
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        ctx.drawImage(
          img,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height
        );
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
    });

  // Convert to grayscale
  const convertToGrayScale = (dataUrl) =>
    new Promise((resolve) => {
      const img = new Image();
      img.src = dataUrl;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
          data[i] = avg;
          data[i + 1] = avg;
          data[i + 2] = avg;
        }
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
    });

  // const cleanSignature = (dataUrl) =>
  //   new Promise((resolve) => {
  //     const img = new Image();
  //     img.src = dataUrl;
  //     img.onload = () => {
  //       const canvas = document.createElement("canvas");
  //       const ctx = canvas.getContext("2d");

  //       canvas.width = img.width;
  //       canvas.height = img.height;

  //       // Draw base with high contrast grayscale
  //       ctx.filter = "brightness(120%) contrast(250%) grayscale(100%)";
  //       ctx.drawImage(img, 0, 0);

  //       const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  //       const data = imageData.data;

  //       for (let i = 0; i < data.length; i += 4) {
  //         const brightness = data[i]; // grayscale so R = G = B

  //         // If pixel is light → make transparent
  //         if (brightness > 190) {
  //           data[i + 3] = 0; // alpha = 0 (transparent)
  //         } else {
  //           // Dark pixels become solid black
  //           data[i] = 0;
  //           data[i + 1] = 0;
  //           data[i + 2] = 0;
  //           data[i + 3] = 255;
  //         }
  //       }

  //       ctx.putImageData(imageData, 0, 0);
  //       resolve(canvas.toDataURL("image/png")); // return transparent PNG
  //     };
  //   });

  const cleanSignature = (dataUrl) =>
    new Promise((resolve) => {
      const img = new Image();
      img.src = dataUrl;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
  
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
  
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
  
        // STEP 1: find background brightness average
        let bgSum = 0;
        let bgCount = 0;
  
        for (let i = 0; i < data.length; i += 4) {
          const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          if (gray > 180) {
            bgSum += gray;
            bgCount++;
          }
        }
  
        const bgAvg = bgCount ? bgSum / bgCount : 255;
        const threshold = bgAvg - 35; // dynamic threshold
  
        // STEP 2: clean image
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
  
          if (a < 10) {
            data[i + 3] = 0;
            continue;
          }
  
          const gray = 0.299 * r + 0.587 * g + 0.114 * b;
  
          // remove notebook lines + background
          if (gray > threshold) {
            data[i + 3] = 0;
          } else {
            // strengthen ink
            data[i] = 0;
            data[i + 1] = 0;
            data[i + 2] = 0;
            data[i + 3] = 255;
          }
        }
  
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
    });

  const getCroppedImage = async () => {
    const cropped = await cropImage(imageSrc, croppedAreaPixels);
    const grayscale = await convertToGrayScale(cropped);
    const white = await cleanSignature(cropped);

    setFinalOptions([
      { type: "original", label: "Normal", image: cropped },
      { type: "gray", label: "Grayscale", image: grayscale },
      { type: "white", label: "Black&White", image: white },
    ]);

    setSelectedOption("original");
    setCropMode(false);
  };

  const dataURLtoFile = (dataUrl, filename) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
    return new File([u8arr], filename, { type: mime });
  };

  const handleSubmit = () => {
    const chosen = finalOptions.find((opt) => opt.type === selectedOption);
    const file = dataURLtoFile(chosen.image, "signature.jpeg");
    onAdd(file);
    onClose();
  };

  useEffect(() => {
    if (!hasFetched.current && Ontrigger) {
      hasFetched.current = true;
      setTimeout(() => handleImageClick(), 300);
    }
  }, []);

  return (
    <>
      <div className="modal-backdrop show" onClick={onClose}></div>
      <div className="modal show" style={{ display: "block" }}>
        <div
          className="modal-dialog modal-md modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h6>{cropMode ? "Crop Signature" : "Choose Signature Style"}</h6>
              <button className="btn-close" onClick={onClose}></button>
            </div>

            <div className="modal-body bg-light">
              {/* Cropper UI */}
              {cropMode && (
                <div style={{ height: 250 }}>
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={4 / 1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>
              )}

              {/* Preview Options */}
              {!cropMode && finalOptions.length > 0 && (
                <div className="row">
                  {finalOptions.map((opt, i) => (
                    <div key={i} className="col-6">
                      <label
                        className={`signature-option ${
                          selectedOption === opt.type ? "active" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          hidden
                          name="signatureStyle"
                          checked={selectedOption === opt.type}
                          onChange={() => setSelectedOption(opt.type)}
                        />
                        <img src={opt.image} className="signature-preview" />
                        <span>{opt.label}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {/* File Input */}
              {!cropMode && finalOptions.length === 0 && (
                <div
                  className="profile-img-containers text-center position-relative w-100"
                  onClick={handleImageClick}
                >
                  {imagePreview === "/assets/images/avatars/default.jpg" ? (
                    <div className="d-flex justify-content-center ">
                      <img
                        src={imagePreview}
                        style={{
                          width: "130px",
                          // maxHeight: "90px",
                          // aspectRatio: "4/1",
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "2px solid #ddd",
                          cursor: "pointer",
                        }}
                      />
                    </div>
                  ) : (
                    <img
                      src={imagePreview}
                      style={{
                        width: "100%",
                        //   maxHeight: "90px",
                        aspectRatio: "4/1",
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "2px solid #ddd",
                        cursor: "pointer",
                      }}
                    />
                  )}
                  <div className="overlay-icon">
                    <PlusIcon/>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </div>

            <div className="modal-footer">
              {cropMode ? (
                <button className="btn btn-primary" onClick={getCroppedImage}>
                  Crop & Continue
                </button>
              ) : (
                finalOptions.length > 0 && (
                  <button className="btn btn-success" onClick={handleSubmit}>
                    Save Signature
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style>{`
        .signature-option {
          border: 2px solid #ddd;
          border-radius: 6px;
          padding: 5px;
          display: block;
          text-align: center;
          cursor: pointer;
          transition: .3s;
        }
        .signature-option.active {
          border-color: #0d6efd;
        }
        .signature-option.active span {
          color: #0d6efd;
          font-weight: bold;
        }
        .signature-preview {
          width: 100%;
          border-radius: 6px;
        }
      `}</style>
    </>
  );
};

export default AddSignature;
